/** @jsxRuntime classic */
/** @jsx jsx */

import { useState } from 'react';
import { css, jsx } from '@emotion/react';
import {
  Button,
  Colors,
  Dropdown,
  FloatingMenu,
  SkeletonBlock,
  Typography,
} from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../../constants';
import { Template, usePopularTemplates } from '../../../graphql/queries';
import { SectionName, useAvroTracking } from '../../../observability';
import { id } from '../../../utils/id';
import { CustomButton } from '../../CustomButton';
import { TemplateCard } from './TemplateCard';

export type BackgroundColor =
  | typeof COLOR_KEYS.blue10
  | typeof COLOR_KEYS.cream
  | typeof COLOR_KEYS.neutral100
  | typeof COLOR_KEYS.neutral200;

type Role = {
  href: string;
  canonicalId?: string;
  name: string;
  templates?: Template[];
};

type Props = {
  backgroundColor: BackgroundColor;
  ctaButtonText?: string;
  defaultTemplates: Template[];
  title: string;
  roles: Role[];
  defaultCtaHref?: string;
};

const Styles = labelStyles('PopularTemplatesSection', {
  root: (backgroundColor: BackgroundColor) =>
    css({
      backgroundColor: Colors[backgroundColor],
      padding: '55px 20px 45px 20px',

      [MEDIA_MIN_WIDTH.small]: {
        padding: '55px 20px 50px 20px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: '55px 35px 85px 35px',
      },
    }),

  title: css({
    ...Typography.pageHeader7,
    color: Colors.earth,
    textAlign: 'center',
    marginBottom: '24px',

    [MEDIA_MIN_WIDTH.small]: {
      ...Typography.pageHeader5,
      marginBottom: '32px',
    },
  }),

  dropdown: css({
    width: '100%',
    margin: '0 auto 32px',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '492px',
      marginBottom: '40px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '570px',
      marginBottom: '48px',
    },
  }),

  templates: (isExpanded: boolean, expandedHeight: number) => [
    css({
      height: '345px',
      display: 'flex',
      justifyContent: 'flex-start',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: '32px',
      paddingBottom: '8px',
      overflow: 'hidden',
      transition: 'height 250ms ease-out',

      [MEDIA_MIN_WIDTH.small]: {
        height: 'auto',
        flexDirection: 'row',
        justifyContent: 'center',
        marginBottom: '40px',
        overflow: 'visible',
      },

      [MEDIA_MIN_WIDTH.large]: {
        marginBottom: '48px',
      },
    }),
    isExpanded &&
      withModifier(
        'expanded',
        css({
          height: expandedHeight,

          [MEDIA_MIN_WIDTH.small]: {
            height: 'auto',
          },
        }),
      ),
  ],

  loaderCard: css({
    width: 'calc(100% - 2px)',
    flexShrink: 0,
    border: `1px solid ${Colors.neutral300}`,

    '& + &': {
      marginTop: '20px',
    },

    [MEDIA_MIN_WIDTH.small]: {
      flexShrink: 1,

      '&:nth-child(3)': {
        display: 'none',
      },

      '& + &': {
        marginTop: 0,
        marginLeft: '30px',
      },
    },

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '370px',

      '&:nth-child(3)': {
        display: 'block',
      },
    },
  }),

  cta: css({
    width: '100%',
    margin: '0 auto',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '216px',
      minWidth: 'max-content',
    },

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '372px',
    },
  }),

  loadMoreButton: css({
    width: '100%',

    '& > button': {
      color: `${Colors.neutral800} !important`,
      border: `1px solid ${Colors.neutral800} !important`,
    },

    [MEDIA_MIN_WIDTH.small]: {
      display: 'none',
    },
  }),

  exploreRolesButton: (isExpanded: boolean) =>
    css({
      width: '100%',
      display: isExpanded ? 'inline-block' : 'none',

      [MEDIA_MIN_WIDTH.small]: {
        display: 'inline-block',
      },
    }),
});

export function PopularTemplatesSection({
  backgroundColor = COLOR_KEYS.cream,
  ctaButtonText = 'Explore Roles page',
  defaultCtaHref,
  defaultTemplates,
  title,
  roles,
}: Props) {
  const { emitCTAClickEvent } = useAvroTracking();
  const [selectedRole, setSelectedRole] = useState<Role>();
  const [isExpanded, setIsExpanded] = useState(false);
  const [templatesId] = useState(id('templates-'));

  const { data, loading } = usePopularTemplates({
    variables: { roleCanonicalId: selectedRole?.canonicalId },
    skip: !!selectedRole?.templates?.length,
  });

  const manuallyInputTemplates = selectedRole
    ? selectedRole.templates
    : defaultTemplates;

  const templates = manuallyInputTemplates?.length
    ? manuallyInputTemplates
    : data;

  const ctaHref = selectedRole?.href || defaultCtaHref;

  const expandedHeight =
    templates?.length > 0
      ? templates.length * 345 + (templates.length - 1) * 20
      : 345;

  return (
    <div css={Styles.root(backgroundColor)}>
      <h2 css={Styles.title}>{title}</h2>
      <div css={Styles.dropdown}>
        <Dropdown
          ariaLabel="Role"
          getLabelForItem={(role) => role.name}
          items={roles}
          menuAriaLabel="List of available roles"
          onChange={setSelectedRole}
          placeholder="Explore by role"
          renderFloatingMenu={(props: any) => (
            <FloatingMenu {...props} visibleItems={roles.length} />
          )}
        />
      </div>
      {loading ? (
        <div css={Styles.templates(isExpanded, expandedHeight)}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div css={Styles.loaderCard} key={`skeleton-card-${i}`}>
              <SkeletonBlock height={345} width="100%" />
            </div>
          ))}
        </div>
      ) : (
        <div
          css={Styles.templates(isExpanded, expandedHeight)}
          id={templatesId}
        >
          {templates?.map((template, index) => {
            return (
              template && (
                <TemplateCard
                  isHiddenOnMobile={index > 0 && !isExpanded}
                  key={`${template.title}-${index}`}
                  services={template.services}
                  title={template.title}
                  url={template.url}
                />
              )
            );
          })}
        </div>
      )}
      <div css={Styles.cta}>
        <div css={Styles.loadMoreButton}>
          {!isExpanded && templates?.length > 1 && (
            <Button
              ariaControls={templatesId}
              ariaExpanded={isExpanded}
              color="secondary"
              isFullWidth={true}
              onClick={() => setIsExpanded(true)}
            >
              Load more
            </Button>
          )}
        </div>
        {ctaHref && (
          <div css={Styles.exploreRolesButton(isExpanded)}>
            <CustomButton
              href={ctaHref}
              isFullWidth={true}
              onClick={() =>
                emitCTAClickEvent({
                  href: ctaHref,
                  section: SectionName.PopularTemplatesSection,
                  text: ctaButtonText,
                })
              }
            >
              {ctaButtonText}
            </CustomButton>
          </div>
        )}
      </div>
    </div>
  );
}
